import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import {
  Character,
  AbilityScores,
  CharacterSheetInterface,
  RolledValue
} from "@dndModels";
import { AbilityScoreType } from "@dndModels/Types";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterAbilityScoresButton from "@dndComponents/character/ability_scores/CharacterAbilityScoresButton";
import CharacterAbilityScore from "@dndComponents/character/ability_scores/CharacterAbilityScore";
import Roller from "@dndComponents/other/Roller";
import ModalOrDrawer from "@components/display/ModalOrDrawer";

const ABILITY_SCORE_DATA: { [ability_score: string]: { name: string; description: string } } = {
  "STR": { name: "Strength", description: "Physical might" },
  "DEX": { name: "Dexterity", description: "Agility, reflexes, and balance" },
  "CON": { name: "Constitution", description: "Health and stamina" },
  "INT": { name: "Intelligence", description: "Reasoning and memory" },
  "WIS": { name: "Wisdom", description: "Perceptiveness and mental fortitude" },
  "CHA": { name: "Charisma", description: "Confidence, poise, and charm" }
};

const ABILITY_SCORES: AbilityScoreType[] = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  horizontal?: boolean;
  shared: boolean;
}

// const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterAbilityScores({
  character,
  char_interface,
  horizontal = false,
  shared,
}: Props) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [popoverAbility, setPopoverAbility] = useState<AbilityScoreType | "">("");
  const [drawerAbility, setDrawerAbility] = useState<AbilityScoreType | "">("");
  const [flashing, setFlashing] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const calcFlashing = useCallback(() => {
    const char = character;
    let new_flashing = false;
    if (!character.sidekick) {
      if (character.ability_score_gen_method === "Point Buy") {
        new_flashing = calcPoints(char.base_ability_scores, char.character_options.points, char.character_options.point_mapping) !== 0;
      } else if (character.ability_score_gen_method === "Standard Array") {
        new_flashing = calcRemaining(char.base_ability_scores, char.character_options.standard_array).length > 0;
      }
    }
    setFlashing(new_flashing);
  }, []);

  const modalClosed = () => {
    setTimeout(() => calcFlashing(), 1000);
  };

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, modalClosed);
    calcFlashing();
  }, [calcFlashing]);

  const calcPoints = (base_ability_scores: AbilityScores, options_points: number, POINTS_FOR_NUM_MAP: { [ability_score: number]: number }) => {
    let pts = options_points;
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.strength];
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.dexterity];
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.constitution];
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.intelligence];
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.wisdom];
    pts -= POINTS_FOR_NUM_MAP[base_ability_scores.charisma];
    return pts;
  };

  const calcRemaining = (base_ability_scores: AbilityScores, options_standard_array: number[]) => {
    const remaining = [...options_standard_array];
    const values: number[] = [];
    const fields = ["strength", "dexterity", "constitution", "intelligence", "wisdom", "charisma"] as const;
    fields.forEach(f => { if (base_ability_scores[f] !== 0) values.push(base_ability_scores[f]); });
    let pos = 0;
    while (pos < remaining.length && values.length > 0) {
      const value = remaining[pos];
      if (values.includes(value)) {
        remaining.splice(pos, 1);
        const idx = values.indexOf(value);
        values.splice(idx, 1);
      } else {
        pos++;
      }
    }
    return remaining;
  };

  const handleRoll = (ability: AbilityScoreType, rolled: RolledValue[], anchorEl: HTMLDivElement) => {
    setRoll(rolled);
    setPopoverAbility(ability);
    setPopoverAnchorEl(anchorEl);
  };

  const renderDetails = (ability: AbilityScoreType | "") => {
    const data = ABILITY_SCORE_DATA[ability];
    if (!data) return <></>;
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>{data.name}</Grid>
        <Grid
          size={{
            xs: 12
          }}>{data.description}</Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={0} direction="row" className="curved_border bg_black_transp">
      <Grid
        size={{
          xs: 12
        }}>
        <CharacterAbilityScoresButton
          character={character}
          className={flashing && !shared ? "Flashing" : ""}
          disabled={shared && !character.sidekick}
          onChange={() => {
            calcFlashing();
            character.subscription_holder.recalculate();
            character.changed();
          }}
        />
      </Grid>
      {ABILITY_SCORES.map(ability => (
        <CharacterAbilityScore
          key={ability}
          ability={ability}
          character={character}
          char_interface={char_interface}
          horizontal={horizontal}
          shared={shared}
          onRoll={handleRoll}
          onOpenDrawer={setDrawerAbility}
        />
      ))}
      <ModalOrDrawer
        open={drawerAbility !== ""}
        onClose={() => {
          setDrawerAbility("");
          character.subscription_holder.modalClosed();
        }}
      >
        <span>
          {drawerAbility !== "" && renderDetails(drawerAbility)}
        </span>
      </ModalOrDrawer>
      <Popover
        open={popoverAnchorEl !== null && popoverAbility !== ""}
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAbility("");
          setPopoverAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {popoverAnchorEl !== null && popoverAbility !== "" && (
          <Roller name={popoverAbility} rolled_values={roll} type="Ability Check" />
        )}
      </Popover>
    </Grid>
  );
}